import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useLocation
} from 'react-router-dom';
import { useAppSelector } from './redux/hooks';
import GenericLayout from './layouts/GenericLayout/GenericLayout';
import Users from './pages/users/user-listing';
import { applications } from './application';
import { IApplication } from './IApplication';
import Images from './pages/images/image-listing';
import PagesLisings from './pages/pages/page-listing';
import NewsLisings from './pages/news/news-listing';
import { AddNewItemPage } from './pages/pages/add-page';
import EditPage from './pages/pages/edit-page';
import EditFooter from './pages/configuration_pages/edit_footer';
import EditHeader from './pages/configuration_pages/edit_header';
import ConfigListing from './pages/configuration_pages/ConfigListing';
import { AddNewItem } from './pages/news/add-news';
import { EditNewItem } from './pages/news/edit-news';
import ProfileDetail from './pages/users/details_page';
import JobListing from './pages/jobs/job-listing';
export const Protected: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get authentication status and current user from the Redux store.
  const isAuthenticated = useAppSelector((s) => s.user.isAuthenticated);
  const currentUser = useAppSelector((state) => state.user);

  // Memoize the applications object so that it remains stable unless currentUser changes.
  const userApplications = useMemo(() => {
    return applications;
  }, [currentUser]);

  // Memoize the keys of the applications object.
  const applicationKeys = useMemo(() => {
    return Object.keys(userApplications);
  }, [userApplications]);

  // Local state for the currently selected application, its pages, and its links.
  const [currentApp, setCurrentApp] = useState<IApplication | null>(null);
  const [pages, setPages] = useState<string[]>([]);
  const [links, setLinks] = useState<string[]>([]);

  // Redirect to login if the user is not authenticated.
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Determine the current app based on the URL.
  useEffect(() => {
    // Split the pathname by '/'.
    const pathParts = location.pathname.split('/');
    let app: IApplication | null = null;

    // If there's at least one path part after the root, try to find a matching application.
    if (pathParts.length > 1 && pathParts[1]) {
      applicationKeys.forEach((appKey) => {
        if (userApplications[appKey].route === `/${pathParts[1]}`) {
          app = userApplications[appKey];
        }
      });
    }

    // If a matching app is found and it’s different from the current one...
    if (app && app !== currentApp) {
      setCurrentApp(app);
      const modules = Object.values(app.modules).filter((mod) => !mod.hidden);
      setPages(modules.map((mod) => mod.title));
      setLinks(modules.map((mod) => `${app.route}${mod.route}`));
    }
    // If no app was found but we have at least one application,
    // set the default to the first application (only if currentApp is not already set).
    else if (!app && applicationKeys.length > 0 && !currentApp) {
      const defaultApp = userApplications[applicationKeys[0]] as IApplication;
      setCurrentApp(defaultApp);
      const modules = Object.values(defaultApp.modules).filter((mod) => !mod.hidden);
      setPages(modules.map((mod) => mod.title));
      setLinks(modules.map((mod) => `${defaultApp.route}${mod.route}`));
    }
    // Otherwise, if the URL no longer matches the current app but currentApp is set,
    // you might want to clear or update it here if desired.
  }, [location.pathname, applicationKeys, userApplications, currentApp]);

  // If not authenticated, render nothing.
  if (!isAuthenticated) return null;
  return (
    <>
      <Routes>
        <Route
          path=""
          element={
            <GenericLayout
              pages={pages}
              links={links}
              appName={currentApp?.title || ''}
            />
          }
        >
          <Route
            path="/web-builder"
            element={<Navigate to="/web-builder/images" />}
          />
          <Route path="/web-builder/images" element={<Images />} />
          <Route path="/web-builder/users" element={<Users />} />
          <Route path="/web-builder/jobs" element={<JobListing />} />
          <Route path="/profile" element={<ProfileDetail />} />
          <Route path="/web-builder/pages/add" element={<AddNewItemPage />} />
          <Route
            path="/web-builder/config_listing"
            element={<ConfigListing />}
          />
          <Route path="/web-builder/edit-footer" element={<EditFooter />} />
          <Route path="/web-builder/edit-header" element={<EditHeader />} />
          <Route path="/web-builder/pages/edit/:id" element={<EditPage />} />
          <Route path="/web-builder/pages" element={<PagesLisings />} />
          <Route path="/web-builder/news" element={<NewsLisings />} />
          <Route path="/web-builder/news/add" element={<AddNewItem />} />
          <Route path="/web-builder/news/edit/:id" element={<EditNewItem />} />

          <Route path="*" element={<div>404 - Page not found</div>} />
        </Route>
      </Routes>
    </>
  );
};
